import { Navigate, Route, Routes } from "react-router-dom";
import HomeScreenSidebar from "./experience/sidebar/HomeScreenSidebar";
import PrivateRoute from "./experience/auth/PrivateRoute";
import {
  BillingAdminWrapper,
  CoordinatorWrapper,
  ProviderWrapper,
} from "./experience/generic/AppRoutesWrapper";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";
import { useEffect } from "react";
import { currentConfiguration } from "./service/memory/recoil/configurationstore";
import IntentType from "./intentexecutor/utils/IntentType";
import configurationKeys from "./service/memory/recoil/featurekeys";
import { getContext } from "./intentexecutor/contextcreator/ContextCreator";
import { mapIntentToRequest } from "./service/network/RequestMapper";
import { performPostRequest } from "./service/network/NetworkService";
import {
  iconsData,
  iconsLoaded,
  themeData,
} from "./service/memory/recoil/theme";
import "./inputstyle.css";
import { getApiUrl } from "./service/network/UrlMapper";
import "bootstrap/dist/css/bootstrap.min.css";
import CheckUserInactivity from "./experience/timeout/CheckUserInactivity";
import { coordinatorRoutes, routes, volunteerRoutes } from "./utils/userRoutes";
import { userDetails } from "./components/recoil/userDataRecoil";
import { useAuth } from "./experience/generic/hooks/useAuth";
import { deferredPromptData } from "./components/recoil/deferredPromptRecoil";
import ScrollToTop from "./utils/scrolltop";
import { HelmetProvider } from "react-helmet-async";
import { useGetOrgIdentifier } from "./experience/generic/hooks/useGetOrgIdentifier";
import { officeLabel } from "./components/recoil/officeLabelRecoil";
import LocalStorageHandler from "./service/memory/handler/LocalStorageHandler";
import MemoryType from "./service/memory/enum/MemoryType";
import BillingDashboard from "./billing/pages/BillingDashboard";

const App = () => {
  const setConfiguration = useSetRecoilState(currentConfiguration);
  const setTheme = useSetRecoilState(themeData);
  const setIcons = useSetRecoilState(iconsData);
  const setIconsLoaded = useSetRecoilState(iconsLoaded);
  const setDeferredPrompt = useSetRecoilState(deferredPromptData);
  const userInformation = useRecoilValue(userDetails);
  const setUserInformation = useSetRecoilState(userDetails);
  const isLoggedIn = useAuth();
  const organizationIdentifier = useGetOrgIdentifier();
  const [label, setLabel] = useRecoilState(officeLabel);

  const loadConfiguration = async () => {
    try {
      const payload = {
        configurationKey: configurationKeys,
      };
      const request = mapIntentToRequest(
        IntentType.GET_CONFIGURATION,
        payload,
        getContext()
      );
      const urlMapper = getApiUrl();
      const result = await performPostRequest(
        urlMapper[IntentType.GET_CONFIGURATION],
        JSON.stringify(request)
      );
      const response = await result.json();
      const configurationKeysAndValue = {};
      // To ensure that only the configuration has been returned from the configuration store the if
      // condition is present.
      if (configurationKeys.length === response.length) {
        for (const item of response) {
          const configurationValue = item?.configurationValue;
          configurationKeysAndValue[item?.configurationKey] =
            JSON.parse(configurationValue);
        }
        setConfiguration(configurationKeysAndValue);
      }
    } catch (ignored) {}
  };

  const loadTheme = async () => {
    try {
      const payload = {
        configurationKey: ["niramaya.organization.theme", "niramaya.organization.icons"],
      };
      const request = mapIntentToRequest(
        IntentType.GET_CONFIGURATION,
        payload,
        {
          organizationIdentifier:
            userInformation?.organizationIdentifier || organizationIdentifier,
        }
      );
      const result = await performPostRequest(
        await getApiUrl()[IntentType.GET_CONFIGURATION],
        JSON.stringify(request)
      );
      const response = await result.json();
      const themeValue = JSON.parse(response[0].configurationValue);
      const icons = JSON.parse(response[1].configurationValue);
      setTheme(themeValue);
      setIcons(icons);
      applyColorsToRoot();
      applyDocumentTitle(themeValue);
      setIconsLoaded(true);

      function applyColorsToRoot() {
        const root = document.documentElement;
        const colorScheme = themeValue.colorScheme;
        Object.keys(colorScheme).forEach((key) => {
          root.style.setProperty(`--${key}`, colorScheme[key]);
        });
      }
    } catch (ignored) {
      applyDocumentTitle();
    }
  };

  function applyDocumentTitle(themeValue = {}) {
    let hostTitle = themeValue?.title;
    if (!hostTitle) {
      hostTitle = "Patient Data Vault";
    }
    document.title = hostTitle;

    let link = document.querySelector("link[rel~='icon']");
    if (!link) {
      link = document.createElement("link");
      link.rel = "icon";
      document.head.appendChild(link);
    }
    link.href = themeValue?.assets?.favicon || "pdvfavicon.svg";
  }

  useEffect(() => {
    if (isLoggedIn && !userInformation?.userIdentifier) {
      const storedUser = new LocalStorageHandler().read(MemoryType.USER);
      if (storedUser?.userIdentifier) {
        setUserInformation(storedUser);
      }
    }
  }, [isLoggedIn, setUserInformation, userInformation?.userIdentifier]);

  useEffect(() => {
    if (isLoggedIn || userInformation?.userIdentifier) {
      loadConfiguration();
      loadTheme();
    } else {
      applyDocumentTitle();
    }
  }, [isLoggedIn, currentConfiguration, userInformation]);

  useEffect(() => {
    const handleBeforeInstallPrompt = (event) => {
      setDeferredPrompt(event);
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
    };
  }, [setDeferredPrompt]);

  useEffect(() => {
    const getLabel = async () => {
      const payload = {
        intent: "get_configuration",
        payload: {
          configurationKey: ["niramaya.teams.label"],
        },
        context: {
          organizationIdentifier,
        },
      };
      try {
        const result = await performPostRequest(
          getApiUrl()[IntentType.GET_CONFIGURATION],
          JSON.stringify(payload),
        );
        const response = await result.json();
        const parsedLabel = JSON.parse(
          response[0].configurationValue || "{}",
        );
        setLabel(parsedLabel?.label || "Team");
      } catch (err) {
        console.error(err);
      }
    };

    if (!label) {
      getLabel();
    }
  }, [label, organizationIdentifier, setLabel]);

  return (
    <div>
    <HelmetProvider>
      <ScrollToTop />
      {(isLoggedIn || userInformation?.userIdentifier) && (
        <CheckUserInactivity />
      )}
      <Routes>
        {routes.map((route) => (
          <Route path={route.path} element={route.element} key={route.path} />
        ))}

        {/* (Care/Volunteer) Coordinator Routes */}
        <Route
          path="/carecoordinator"
          element={
            <PrivateRoute>
              <CoordinatorWrapper>
                <HomeScreenSidebar />
              </CoordinatorWrapper>
            </PrivateRoute>
          }
        >
          <Route
            index
            element={<Navigate to="/carecoordinator/homepage" replace />}
          />
          {coordinatorRoutes.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}
        </Route>

        {/* Care Provider Routes */}
        <Route
          path="/careprovider"
          element={
            <PrivateRoute>
              <ProviderWrapper>
                <HomeScreenSidebar />
              </ProviderWrapper>
            </PrivateRoute>
          }
        >
          <Route
            index
            element={<Navigate to="/careprovider/homepage" replace />}
          />
          {volunteerRoutes.map((route) => (
            <Route path={route.path} element={route.element} key={route.path} />
          ))}
        </Route>

        {/* Billing Admin Routes */}
        <Route
          path="/billingAdmin/billing"
          element={
            <PrivateRoute>
              <BillingAdminWrapper>
                <BillingDashboard />
              </BillingAdminWrapper>
            </PrivateRoute>
          }
        />
        
      </Routes>
    </HelmetProvider>
    </div>
  );
};

export default App;
